import { useToast } from '/@/core/elements/toast'
import { useRollbar } from '/@/core/rollbar'
import { reactive, toRefs } from 'vue'
import { createDelayedTimer } from '/@/utils/timing'

interface State {
  active: boolean
  visible: boolean
  hiding: boolean
  showText: boolean
  text: string
}

interface ShowConfig {
  delay?: number
  textDelay?: number
  text?: string
  timeout?: number
  errorTitle?: string
  errorMessage?: string
}

const FADE_OUT_MS = 100
const MIN_VISIBLE_MS = 300

const state = reactive<State>({
  active: false,
  visible: false,
  hiding: false,
  showText: false,
  text: '',
})

const spinnerTimer = createDelayedTimer(MIN_VISIBLE_MS)
const textTimer = createDelayedTimer(MIN_VISIBLE_MS)
let fadeTimer: ReturnType<typeof setTimeout> | undefined
let timeoutHandle: ReturnType<typeof setTimeout> | number | undefined

function doHide() {
  clearTimeout(fadeTimer)
  if (state.visible) {
    state.hiding = true
    fadeTimer = setTimeout(() => {
      state.active = false
      state.visible = false
      state.hiding = false
      state.showText = false
      state.text = ''
    }, FADE_OUT_MS)
  } else {
    state.active = false
    state.showText = false
    state.text = ''
  }
}

function scheduleShow(delay = 200, textDelay = 3000) {
  clearTimeout(fadeTimer)
  spinnerTimer.cancel()
  textTimer.cancel()
  state.hiding = false
  state.active = true
  spinnerTimer.scheduleShow(() => {
    state.visible = true
  }, delay)
  textTimer.scheduleShow(() => {
    state.showText = true
  }, textDelay)
}

function scheduleHide() {
  clearTimeout(fadeTimer)
  spinnerTimer.cancel()
  textTimer.cancel()

  // Spinner was never shown — nothing visible, hide immediately
  if (!spinnerTimer.shown) {
    doHide()
    return
  }

  const remaining = Math.max(
    spinnerTimer.remaining,
    state.showText ? textTimer.remaining : 0,
  )

  if (remaining > 0) {
    fadeTimer = setTimeout(doHide, remaining)
  } else {
    doHide()
  }
}

function create() {
  const rollbar = useRollbar()
  const toast = useToast()

  function triggerTimeout(errorTitle: string, errorMessage?: string) {
    scheduleHide()
    toast.showPrecanned('network-error', {
      message: errorMessage,
    })
    // eslint-disable-next-line no-console
    console.log(errorTitle, errorMessage)
    rollbar.error(errorTitle, {
      errorMessage,
    })
  }

  function hide() {
    clearTimeout(timeoutHandle)
    timeoutHandle = undefined
    scheduleHide()
  }

  function show(conf: ShowConfig = {}) {
    state.text = conf.text ?? ''
    scheduleShow(conf.delay, conf.textDelay)
    if (conf.timeout) {
      clearTimeout(timeoutHandle)
      timeoutHandle = setTimeout(
        triggerTimeout,
        conf.timeout,
        conf.errorTitle,
        conf.errorMessage,
      )
    }
  }

  async function waitTask(fn: () => unknown, conf?: ShowConfig) {
    show(conf)
    try {
      await fn()
    } catch (e) {
      toast.showPrecanned('network-error', {
        message: conf?.errorMessage,
      })
      // eslint-disable-next-line no-console
      console.log(e)
      rollbar.error(
        conf?.errorTitle ??
          `Operation failed: ${(e as Error).message ?? 'unknown error'}`,
        {
          jsErrorMessage: (e as Error).message,
          jsErrorStack: (e as Error).stack,
        },
      )
      throw e
    } finally {
      hide()
    }
  }

  return reactive({
    ...toRefs(state),
    show,
    hide,
    waitTask,
  })
}

let cachedApi: ReturnType<typeof create>

export function useLoading() {
  if (!cachedApi) {
    cachedApi = create()
  }
  return cachedApi
}

// Escape hatch for contexts outside the Vue component lifecycle (router guards,
// Apollo links, etc.) where composables cannot be called. Prefer useLoading()
// inside components and composables.
export const __api = {
  hide() {
    clearTimeout(timeoutHandle)
    timeoutHandle = undefined
    scheduleHide()
  },
  show(conf: ShowConfig = {}) {
    // Delegate to the full API when available (always the case at runtime);
    // fall back to a basic show before first mount.
    if (cachedApi) {
      cachedApi.show(conf)
    } else {
      state.text = conf.text ?? ''
      scheduleShow(conf.delay, conf.textDelay)
    }
  },
}
